/**
 * Shared utility for extracting tag-based CSS from stylesheets.
 * This module provides functions that work in both client and server environments
 * to extract CSS rules that apply to HTML tag selectors.
 */

import {isTagSelector} from './htmlTags';

/**
 * Interface for a CSS rule with selector text and CSS text
 */
export interface CssRule {
  selectorText: string;
  cssText: string;
  type: number; // 1 for style rule, 4 for media rule, etc.
}

/**
 * Interface for a media rule that contains nested CSS rules
 */
export interface MediaRule extends CssRule {
  cssRules: CssRule[];
  conditionText: string;
}

/**
 * Extracts tag-based CSS rules from a collection of CSS rules.
 * This function works with both client-side StyleSheetList and server-side parsed rules.
 * It preserves the original order of CSS rules to maintain correct precedence.
 *
 * @param cssRules Array of CSS rules to process
 * @returns String containing the extracted tag-based CSS
 */
export function extractTagCssFromRules(cssRules: CssRule[]): string {
  let tagCss = '';

  // Process each rule in the original order
  cssRules.forEach((rule) => {
    // Handle style rules (type 1)
    if (rule.type === 1) {
      const selector = rule.selectorText;

      // Check if this is a tag selector
      const isTag = isTagSelector(selector);

      if (isTag) {
        // Add the rule in its original position
        tagCss += rule.cssText + '\n';
      }
    }
    // Handle media rules (type 4)
    else if (rule.type === 4) {
      const mediaRule = rule as MediaRule;
      let mediaRuleCss = '';

      // Extract tag selectors from media rule, preserving their original order
      mediaRule.cssRules.forEach((nestedRule) => {
        if (nestedRule.type === 1) {
          const isTag = isTagSelector(nestedRule.selectorText);

          if (isTag) {
            mediaRuleCss += nestedRule.cssText + '\n';
          }
        }
      });

      // If we found tag selectors in the media rule, add the media query wrapper
      if (mediaRuleCss) {
        tagCss += `@media ${mediaRule.conditionText} {\n${mediaRuleCss}}\n`;
      }
    }
  });

  return tagCss;
}
